import { pmFetchJson } from "@penmate/react-shared/api/pmFetch";
import type { RawMessage } from "@/api/messages";
import { withSessionAttributionContext } from "@/lib/sessionAttribution";

type DraftResponse = { letter: RawMessage };
type DraftsListResponse = { drafts: RawMessage[] };

// Pricing and promo facts are the server's: it computes them on every read
// and never needs them back. Echoing a cached copy on save could persist a
// stale value (a tab kept alive across a server change did exactly that),
// so they never ride in a draft payload, whichever caller built it.
const SERVER_OWNED_DRAFT_KEYS = ["meta", "price", "priceAfterCoupon", "price_after_coupon", "recovery_offer"] as const;

const withoutServerOwnedKeys = (draft: Record<string, unknown>): Record<string, unknown> => {
  const next = { ...draft };
  for (const key of SERVER_OWNED_DRAFT_KEYS) delete next[key];
  return next;
};

const sanitizeDraftPayload = (draft: Record<string, unknown>): Record<string, unknown> => {
  const next: Record<string, unknown> = { ...draft };
  const nested = next.data;
  if (nested && typeof nested === "object") {
    const keys = Object.keys(next).filter((k) => k !== "data");
    if (keys.length === 0 || (keys.length === 1 && keys[0] === "id")) {
      const nestedDraft = { ...(nested as Record<string, unknown>) };
      if (next.id != null && nestedDraft.id == null) nestedDraft.id = next.id;
      return withoutServerOwnedKeys(nestedDraft);
    }
    delete next.data;
  }
  return withoutServerOwnedKeys(next);
};

const normalizeMessage = (m: any): RawMessage => {
  // Angular normalizes by copying `data` to top-level; do the same.
  const data = (m && m.data) || {};
  return { ...data, ...m };
};

export async function fetchDrafts(): Promise<RawMessage[]> {
  const res = await pmFetchJson<DraftsListResponse>("/drafts", { method: "GET" });
  const drafts = (res && (res as any).drafts) || [];
  return Array.isArray(drafts) ? drafts.map(normalizeMessage) : [];
}

export async function saveDraftText(args: {
  draftId: string;
  message: string;
}): Promise<RawMessage> {
  const res = await pmFetchJson<DraftResponse>(`/drafts/${args.draftId}/message`, {
    method: "POST",
    body: { message: args.message },
  });
  return normalizeMessage(res.letter);
}

export async function saveDraft(args: {
  draft: Record<string, unknown>;
}): Promise<RawMessage> {
  const res = await pmFetchJson<DraftResponse>("/drafts", {
    method: "POST",
    body: withSessionAttributionContext({ letter: sanitizeDraftPayload(args.draft) }),
  });
  return normalizeMessage(res.letter);
}
